import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { spacing } from "@styling/sizing";
import type {
  RestCreateLayoutType,
  RestLayout,
} from "@KassaroAB/shared/types/layout";
import type { ProductPageId } from "@KassaroAB/shared/types/ids";
import { FormSection } from "../../components/FormSection";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetProductPagesQuery } from "../../query/productPagesQuery";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const optionalNumber = (value: number | ""): number | undefined =>
  value === "" ? undefined : value;

interface LayoutFormProps {
  formId: string;
  layout?: RestLayout;
  savedTick?: number;
  onSubmit: (data: RestCreateLayoutType) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const LayoutForm = ({
  formId,
  layout,
  savedTick,
  onSubmit,
  onDirtyChange,
}: LayoutFormProps) => {
  const { t } = useTranslation();
  const { scope } = useCatalogScope();
  const { data: productPages } = useGetProductPagesQuery(scope);
  const initialDefaultProductPageId: ProductPageId | "" =
    layout?.defaultProductPageId ?? "";
  const initialTabletColumns: number | "" = layout?.tabletColumns ?? "";
  const initialTabletRows: number | "" = layout?.tabletRows ?? "";
  const initialMobileColumns: number | "" = layout?.mobileColumns ?? "";
  const initialMobileRows: number | "" = layout?.mobileRows ?? "";

  const form = useForm({
    defaultValues: {
      name: layout?.name ?? "",
      defaultProductPageId: initialDefaultProductPageId,
      tabletColumns: initialTabletColumns,
      tabletRows: initialTabletRows,
      mobileColumns: initialMobileColumns,
      mobileRows: initialMobileRows,
    },
    onSubmit: ({ value }) => {
      const payload: RestCreateLayoutType = {
        name: value.name,
        topButtons: layout?.topButtons ?? [],
        bottomButtons: layout?.bottomButtons ?? [],
        leftButtons: layout?.leftButtons ?? [],
        rightButtons: layout?.rightButtons ?? [],
        defaultProductPageId:
          value.defaultProductPageId === ""
            ? null
            : value.defaultProductPageId,
      };
      const tabletColumns = optionalNumber(value.tabletColumns);
      const tabletRows = optionalNumber(value.tabletRows);
      const mobileColumns = optionalNumber(value.mobileColumns);
      const mobileRows = optionalNumber(value.mobileRows);
      if (tabletColumns !== undefined) payload.tabletColumns = tabletColumns;
      if (tabletRows !== undefined) payload.tabletRows = tabletRows;
      if (mobileColumns !== undefined) payload.mobileColumns = mobileColumns;
      if (mobileRows !== undefined) payload.mobileRows = mobileRows;
      onSubmit(payload);
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("layoutForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="defaultProductPageId">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.defaultProductPageId")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value;
                    field.handleChange(next === "" ? "" : next);
                  }}
                  onBlur={field.handleBlur}
                >
                  <option value="">
                    {t("layoutForm.defaultProductPageNone")}
                  </option>
                  {(productPages ?? []).map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.name}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="tabletColumns">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.tabletColumns")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="tabletRows">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.tabletRows")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="mobileColumns">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.mobileColumns")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="mobileRows">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("layoutForm.mobileRows")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
